// Sport Pickleball route rebuild trigger
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/hooks/useAuth";
import { AppErrorBoundary } from "@/components/common/AppErrorBoundary";
import { lazy, Suspense, useEffect } from "react";
import { Loader2 } from "lucide-react";
import { globalFeatureFlags } from '@/lib/featureFlags';
import { coreWebVitals } from '@/lib/coreWebVitals';

import ProtectedRoute from "@/components/layout/ProtectedRoute";
import Header from "@/components/layout/Header";
import Welcome from "./pages/Welcome";
import Projects from "./pages/Projects";
import ProjectDetail from "./pages/ProjectDetail";
import LoginPage from "@/components/auth/LoginPage";
import NotFound from "./pages/NotFound";

import AdminLayout from "@/components/admin/AdminLayout";
import { AdminRouteLoader } from "@/components/admin/AdminRouteLoader";
import AdminProtectedRoute from "@/components/layout/AdminProtectedRoute";
import ScheduleLite from "./pages/schedule-lite/ScheduleLite";
import Items from "./pages/Items";
import ItemsProtectedRoute from "@/components/layout/ItemsProtectedRoute";
import Stakeholders from "./pages/Stakeholders";
import { StakeholdersProtectedRoute } from "@/components/layout/StakeholdersProtectedRoute";
import Subcons from "./pages/Subcons";
import SubconsProtectedRoute from "@/components/layout/SubconsProtectedRoute";
const SubconsPeriod = lazy(() => import("./pages/SubconsPeriod"));
import SubconsPeriodProtectedRoute from "@/components/layout/SubconsPeriodProtectedRoute";
import Meetings from "./pages/Meetings";
import MeetingProtectedRoute from "@/components/layout/MeetingProtectedRoute";
import Collections from "./pages/Collections";
import CollectionsProtectedRoute from "@/components/layout/CollectionsProtectedRoute";
import AuthCallback from "@/pages/auth/AuthCallback";
import PublicPreview from "@/pages/PublicPreview";
import { ModeIndicator } from "@/components/auth/ModeIndicator";
import Maintenance from "./pages/Maintenance";
import MaintenanceProtectedRoute from "@/components/layout/MaintenanceProtectedRoute";
import { DebugPermissionsOverlay } from "@/components/debug/DebugPermissionsOverlay";
import { RouteTransitionCleanup } from "@/components/system/RouteTransitionCleanup";
import { SmartPrefetchProvider } from "@/components/system/SmartPrefetchProvider";
import { ClickShieldDetector } from "@/components/debug/ClickShieldDetector";
import { FeatureFlagProvider } from '@/contexts/FeatureFlagContext';
import { AuthEventListener } from "@/components/auth/AuthEventListener";
import UserProfileSettings from "@/pages/UserProfileSettings";
import TrashPage from "@/pages/Trash";
const Library = lazy(() => import("@/pages/Library"));
import { ForceRefreshProvider } from "@/components/system/ForceRefreshProvider";
import { preloadChunks } from "@/lib/chunkPreloader";

// Lazy load admin pages for better performance
const AdminDashboard = lazy(() => import("@/pages/admin/AdminDashboard"));
const UserManagement = lazy(() => import("@/pages/admin/UserManagementContainer"));
const StakeholderManagement = lazy(() => import("@/pages/admin/StakeholderManagement"));
const RoleManagement = lazy(() => import("@/pages/admin/RoleManagement"));
const PermissionMatrix = lazy(() => import("@/pages/admin/PermissionMatrix"));
const SimplePermissionManager = lazy(() => import("@/pages/admin/SimplePermissionManager"));
const ProjectManagement = lazy(() => import("@/pages/admin/ProjectManagement"));
const ProjectCategoriesManagement = lazy(() => import("@/pages/admin/ProjectCategoriesManagement"));
const KeyplanSetup = lazy(() => import("@/pages/admin/KeyplanSetup"));

const FeatureFlagsPage = lazy(() => import("@/pages/FeatureFlagsPage"));
const ThumbnailBackfill = lazy(() => import("@/pages/admin/ThumbnailBackfill"));
const RoadmapCategoriesManagement = lazy(() => import("@/pages/admin/RoadmapCategoriesManagement"));
const RoadmapHierarchyManagement = lazy(() => import("@/pages/admin/RoadmapHierarchyManagement"));
const DebugUtilities = lazy(() => import("@/pages/admin/DebugUtilities"));
const SystemControls = lazy(() => import("@/pages/admin/SystemControls"));
const RuleDefaultsManagement = lazy(() => import("@/pages/admin/RuleDefaultsManagement"));
const Site = lazy(() => import("@/pages/Site"));
const SiteProtectedRoute = lazy(() => import("@/components/layout/SiteProtectedRoute"));
const Forms = lazy(() => import("@/pages/Forms"));

// Unified Employee Hub (Phase 5) - EmployeeHub removed, using redirect for backward compatibility
import EmployeeRedirect from "@/components/redirects/EmployeeRedirect";

// Unified People Module (Phase 6 - Gradual Migration)
const People2 = lazy(() => import("@/pages/People2"));

// Shared Public Pages
const SharedPermit = lazy(() => import("@/pages/SharedPermit"));
const SharedBulkPermit = lazy(() => import("@/pages/SharedBulkPermit"));
const SharedDrawing = lazy(() => import("@/pages/SharedDrawing"));
const SharedBulkDrawings = lazy(() => import("@/pages/SharedBulkDrawings"));
const SharedLayout = lazy(() => import("@/pages/SharedLayout"));
const KyThuatKhach = lazy(() => import("@/kit/ky-thuat-thach-cao/ui/KyThuatKhach"));
const TrangKyThuat = lazy(() => import("@/kit/ky-thuat-thach-cao/ui/TrangKyThuat"));
const TrangTongKyThuat = lazy(() => import("@/kit/ky-thuat-thach-cao/ui/TrangTongKyThuat"));

// Survey Module
const Surveys = lazy(() => import("@/pages/Surveys"));
const SurveyForm = lazy(() => import("@/pages/SurveyForm"));
const SurveyManagement = lazy(() => import("@/pages/admin/SurveyManagement"));
const SiteFormManagement = lazy(() => import("@/pages/admin/SiteFormManagement"));
const AISettings = lazy(() => import("@/pages/admin/AISettings"));
const HomeCardsManagement = lazy(() => import("@/pages/admin/HomeCardsManagement"));
const NavigationSettings = lazy(() => import("@/pages/admin/NavigationSettings"));
const AIProjectAdvisor = lazy(() => import("@/pages/AIProjectAdvisor"));
const AIStakeholderAdvisor = lazy(() => import("@/pages/AIStakeholderAdvisor"));
const AIPeopleAdvisor = lazy(() => import("@/pages/AIPeopleAdvisor"));
const SportPickleball = lazy(() => import("@/pages/SportPickleball"));
const PickleballTournament = lazy(() => import("@/pages/sport/PickleballTournament"));
const WorkStandardsPage = lazy(() => import("@/pages/WorkStandardsPage"));
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error: any) => {
        // Don't retry on auth errors or 4xx errors
        if (error instanceof Error && error.message.includes('auth')) return false;
        // Don't retry on 401/403 unauthorized errors
        if (error?.status === 401 || error?.status === 403) return false;
        // Don't retry on 429 rate limit errors
        if (error?.status === 429) return false;
        // Don't retry on 5xx server errors to prevent cascade
        if (error?.status >= 500) return false;
        return failureCount < 1; // Single retry max
      },
      staleTime: 5 * 60 * 1000, // 5 minutes - optimized for navigation speed
      gcTime: 10 * 60 * 1000, // 10 minutes
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 4000), // Faster retries
      refetchOnWindowFocus: false, // Prevent excessive refetching
      refetchOnReconnect: true,    // Enable for connectivity recovery
      refetchOnMount: true,        // Enable for fresh data on navigation
    },
  },
});

const App = () => {
  // Feature flags
  const ENABLE_STAKEHOLDERS = import.meta.env.VITE_ENABLE_STAKEHOLDERS_PAGE !== 'false';
  const ENABLE_SUBCONS_PAGE = import.meta.env.VITE_ENABLE_SUBCONS_PAGE !== 'false';
  const ENABLE_COLLECTIONS_PAGE = import.meta.env.VITE_ENABLE_COLLECTIONS_PAGE !== 'false';
  
  // Initialize Core Web Vitals tracking
  useEffect(() => {
    coreWebVitals.initTracking();
  }, []);
  
  // Preload lazy chunks during browser idle time to prevent Suspense flash
  useEffect(() => {
    if ('requestIdleCallback' in window) {
      (window as any).requestIdleCallback(preloadChunks, { timeout: 3000 });
    } else {
      // Safari fallback - still non-blocking due to dynamic import
      setTimeout(preloadChunks, 100);
    }
  }, []);
  
  // Global error handler for chunk loading errors (stale deployments)
  useEffect(() => {
    const handleChunkError = (event: ErrorEvent) => {
      const isChunkError = 
        event.message?.includes('Failed to fetch dynamically imported module') ||
        event.message?.includes('Loading chunk') ||
        event.message?.includes('ChunkLoadError');
      
      if (isChunkError) {
        event.preventDefault();
        // ✅ Phase 0: DEV logging to identify reload source
        if (import.meta.env.DEV) {
          console.error('🔴 [RELOAD DETECTED] Chunk loading error → forcing page reload', {
            source: 'App.tsx handleChunkError',
            message: event.message,
            timestamp: new Date().toISOString()
          });
        } else {
          console.warn('[App] Chunk loading error detected, reloading...');
        }
        window.location.reload();
      }
    };
    
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      const errorMessage = event.reason?.message || String(event.reason);
      const isChunkError = 
        errorMessage.includes('Failed to fetch dynamically imported module') ||
        errorMessage.includes('Loading chunk') ||
        errorMessage.includes('ChunkLoadError');
      
      if (isChunkError) {
        event.preventDefault();
        // ✅ Phase 0: DEV logging to identify reload source
        if (import.meta.env.DEV) {
          console.error('🔴 [RELOAD DETECTED] Chunk loading rejection → forcing page reload', {
            source: 'App.tsx handleUnhandledRejection',
            message: errorMessage,
            timestamp: new Date().toISOString()
          });
        } else {
          console.warn('[App] Chunk loading rejection detected, reloading...');
        }
        window.location.reload();
      }
    };
    
    window.addEventListener('error', handleChunkError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    
    return () => {
      window.removeEventListener('error', handleChunkError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);
  
  return (
  <AppErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <AuthEventListener />
        <FeatureFlagProvider>
          <ForceRefreshProvider>
          <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter
            future={{
              v7_relativeSplatPath: true
            }}
          >
            <ModeIndicator />
            <DebugPermissionsOverlay />
            {import.meta.env.DEV && <ClickShieldDetector />}
            <RouteTransitionCleanup />
            <SmartPrefetchProvider />
            <Routes>
              <Route path="/auth" element={<LoginPage />} />
              <Route path="/auth/callback" element={<AuthCallback />} />
              <Route path="/public-preview" element={<PublicPreview />} />
              {/* Public share routes - no auth required */}
              <Route path="/share/permit/:token" element={
                <Suspense fallback={<div className="flex items-center justify-center min-h-screen"><Loader2 className="animate-spin" /></div>}>
                  <SharedPermit />
                </Suspense>
              } />
              <Route path="/share/permits/:token" element={
                <Suspense fallback={<div className="flex items-center justify-center min-h-screen"><Loader2 className="animate-spin" /></div>}>
                  <SharedBulkPermit />
                </Suspense>
              } />
              <Route path="/share/drawing/:token" element={
                <Suspense fallback={<div className="flex items-center justify-center min-h-screen"><Loader2 className="animate-spin" /></div>}>
                  <SharedDrawing />
                </Suspense>
              } />
              <Route path="/share/drawings/:token" element={
                <Suspense fallback={<div className="flex items-center justify-center min-h-screen"><Loader2 className="animate-spin" /></div>}>
                  <SharedBulkDrawings />
                </Suspense>
              } />
              <Route path="/share/layout/:token" element={
                <Suspense fallback={<div className="flex items-center justify-center min-h-screen"><Loader2 className="animate-spin" /></div>}>
                  <SharedLayout />
                </Suspense>
              } />
              <Route path="/ky-thuat/:ma" element={
                <Suspense fallback={<div className="flex items-center justify-center min-h-screen"><Loader2 className="animate-spin" /></div>}>
                  <KyThuatKhach />
                </Suspense>
              } />
              <Route path="/ky-thuat-noi-bo" element={
                <ProtectedRoute>
                  <Suspense fallback={<div className="flex items-center justify-center min-h-screen"><Loader2 className="animate-spin" /></div>}>
                    <div className="min-h-screen bg-background">
                      <Header />
                      <TrangTongKyThuat />
                    </div>
                  </Suspense>
                </ProtectedRoute>
              } />
              <Route path="/ky-thuat-noi-bo/:hm" element={
                <ProtectedRoute>
                  <Suspense fallback={<div className="flex items-center justify-center min-h-screen"><Loader2 className="animate-spin" /></div>}>
                    <TrangKyThuat />
                  </Suspense>
                </ProtectedRoute>
              } />
              <Route path="/" element={<ProtectedRoute><Welcome /></ProtectedRoute>} />
              {/* /people cũ → People 2 (20/10/2026). pages/People.tsx + components/hr giữ tới khi dọn theo Track B. */}
              <Route path="/people" element={<Navigate to="/people2" replace />} />
              <Route path="/people2/*" element={
                <ProtectedRoute>
                  <Suspense fallback={<div className="flex items-center justify-center min-h-screen"><Loader2 className="animate-spin" /></div>}>
                    <People2 />
                  </Suspense>
                </ProtectedRoute>
              } />
              {/* CRM Dashboard moved to Stakeholder Management Page */}
              <Route path="/projects" element={<ProtectedRoute><Projects /></ProtectedRoute>} />
              <Route path="/projects/:id" element={<ProtectedRoute><ProjectDetail /></ProtectedRoute>} />
              <Route path="/items" element={<ProtectedRoute><ItemsProtectedRoute><Items /></ItemsProtectedRoute></ProtectedRoute>} />
              <Route path="/maintenance" element={<ProtectedRoute><MaintenanceProtectedRoute><Maintenance /></MaintenanceProtectedRoute></ProtectedRoute>} />
              {ENABLE_STAKEHOLDERS && (
                <Route path="/stakeholders" element={<ProtectedRoute><StakeholdersProtectedRoute><AppErrorBoundary><Stakeholders /></AppErrorBoundary></StakeholdersProtectedRoute></ProtectedRoute>} />
              )}
              {ENABLE_SUBCONS_PAGE && (
                <Route path="/subcons" element={<ProtectedRoute><SubconsProtectedRoute><Subcons /></SubconsProtectedRoute></ProtectedRoute>} />
              )}
              <Route path="/subcons-period" element={
                <ProtectedRoute>
                  <SubconsPeriodProtectedRoute>
                    <Suspense fallback={<div className="flex items-center justify-center min-h-screen"><Loader2 className="animate-spin" /></div>}>
                      <SubconsPeriod />
                    </Suspense>
                  </SubconsPeriodProtectedRoute>
                </ProtectedRoute>
              } />
              <Route path="/meetings" element={<ProtectedRoute><MeetingProtectedRoute><Meetings /></MeetingProtectedRoute></ProtectedRoute>} />

              {ENABLE_COLLECTIONS_PAGE && (
                <Route path="/collections" element={<ProtectedRoute><CollectionsProtectedRoute><Collections /></CollectionsProtectedRoute></ProtectedRoute>} />
              )}
              {/* Trash Page */}
              <Route path="/trash" element={<ProtectedRoute><AppErrorBoundary><TrashPage /></AppErrorBoundary></ProtectedRoute>} />
              {/* Library Page */}
              <Route path="/library" element={
                <ProtectedRoute>
                  <Suspense fallback={<div className="flex items-center justify-center min-h-screen"><Loader2 className="animate-spin" /></div>}>
                    <Library />
                  </Suspense>
                </ProtectedRoute>
              } />
              <Route path="/profile/settings" element={<ProtectedRoute><UserProfileSettings /></ProtectedRoute>} />
              <Route path="/work-standards" element={
                <ProtectedRoute>
                  <Suspense fallback={<div className="flex items-center justify-center min-h-screen"><Loader2 className="animate-spin" /></div>}>
                    <WorkStandardsPage />
                  </Suspense>
                </ProtectedRoute>
              } />
              {/* Survey Module Routes */}
              <Route path="/surveys" element={
                <ProtectedRoute>
                  <Suspense fallback={<div className="flex items-center justify-center min-h-screen"><Loader2 className="animate-spin" /></div>}>
                    <Surveys />
                  </Suspense>
                </ProtectedRoute>
              } />
              <Route path="/surveys/:id" element={
                <ProtectedRoute>
                  <Suspense fallback={<div className="flex items-center justify-center min-h-screen"><Loader2 className="animate-spin" /></div>}>
                    <SurveyForm />
                  </Suspense>
                </ProtectedRoute>
              } />
              <Route path="/admin" element={<ProtectedRoute><AdminProtectedRoute><AdminLayout /></AdminProtectedRoute></ProtectedRoute>}>
                <Route index element={<AdminRouteLoader><AdminDashboard /></AdminRouteLoader>} />
                <Route path="users" element={<AdminRouteLoader><UserManagement /></AdminRouteLoader>} />
                <Route path="stakeholders" element={<AdminRouteLoader><StakeholderManagement /></AdminRouteLoader>} />
                <Route path="roles" element={<AdminRouteLoader><RoleManagement /></AdminRouteLoader>} />
                <Route path="permissions" element={<AdminRouteLoader><PermissionMatrix /></AdminRouteLoader>} />
                {globalFeatureFlags.isEnabled('ENABLE_SIMPLE_PERMISSION_MANAGER') && (
                  <Route path="permissions-new" element={<AdminRouteLoader><SimplePermissionManager /></AdminRouteLoader>} />
                )}
                <Route path="projects" element={<AdminRouteLoader><ProjectManagement /></AdminRouteLoader>} />
                <Route path="categories" element={<AdminRouteLoader><ProjectCategoriesManagement /></AdminRouteLoader>} />
                <Route path="keyplan-setup" element={<AdminRouteLoader><KeyplanSetup /></AdminRouteLoader>} />
                
                <Route path="feature-flags" element={<AdminRouteLoader><FeatureFlagsPage /></AdminRouteLoader>} />
                <Route path="thumbnail-backfill" element={<AdminRouteLoader><ThumbnailBackfill /></AdminRouteLoader>} />
                <Route path="roadmap-categories" element={<AdminRouteLoader><RoadmapCategoriesManagement /></AdminRouteLoader>} />
                <Route path="roadmap-hierarchy" element={<AdminRouteLoader><RoadmapHierarchyManagement /></AdminRouteLoader>} />
                <Route path="debug" element={<AdminRouteLoader><DebugUtilities /></AdminRouteLoader>} />
                <Route path="system-controls" element={<AdminRouteLoader><SystemControls /></AdminRouteLoader>} />
                <Route path="surveys" element={<AdminRouteLoader><SurveyManagement /></AdminRouteLoader>} />
                <Route path="site-forms" element={<Navigate to="/forms?tab=templates" replace />} />
                <Route path="ai-settings" element={<AdminRouteLoader><AISettings /></AdminRouteLoader>} />
                <Route path="rule-defaults" element={<AdminRouteLoader><RuleDefaultsManagement /></AdminRouteLoader>} />
                <Route path="home-cards" element={<AdminRouteLoader><HomeCardsManagement /></AdminRouteLoader>} />
                <Route path="navigation" element={<AdminRouteLoader><NavigationSettings /></AdminRouteLoader>} />
              </Route>
              <Route path="/schedule-lite" element={<ProtectedRoute><ScheduleLite /></ProtectedRoute>} />
              <Route path="/site" element={
                <ProtectedRoute>
                  <Suspense fallback={<div className="flex items-center justify-center min-h-screen"><Loader2 className="animate-spin" /></div>}>
                    <SiteProtectedRoute>
                      <Site />
                    </SiteProtectedRoute>
              </Suspense>
                </ProtectedRoute>
              } />
              <Route path="/forms" element={
                <ProtectedRoute>
                  <Suspense fallback={<div className="flex items-center justify-center min-h-screen"><Loader2 className="animate-spin" /></div>}>
                    <SiteProtectedRoute>
                      <Forms />
                    </SiteProtectedRoute>
                  </Suspense>
                </ProtectedRoute>
              } />
              {/* Employee Redirect (backward compatibility for old /employee/:id URLs) */}
              <Route path="/employee/:id" element={<EmployeeRedirect />} />
              {/* AI Project Advisor - Admin Only */}
              <Route path="/ai-projects" element={
                <ProtectedRoute>
                  <Suspense fallback={<div className="flex items-center justify-center min-h-screen"><Loader2 className="h-8 w-8 animate-spin" /></div>}>
                    <AIProjectAdvisor />
                  </Suspense>
                </ProtectedRoute>
              } />
              {/* AI Stakeholder Advisor - Admin Only */}
              <Route path="/ai-stakeholders" element={
                <ProtectedRoute>
                  <Suspense fallback={<div className="flex items-center justify-center min-h-screen"><Loader2 className="h-8 w-8 animate-spin" /></div>}>
                    <AIStakeholderAdvisor />
                  </Suspense>
                </ProtectedRoute>
              } />
              {/* AI People Advisor - Admin or HR_ACCESS */}
              <Route path="/ai-hr" element={
                <ProtectedRoute>
                  <Suspense fallback={<div className="flex items-center justify-center min-h-screen"><Loader2 className="h-8 w-8 animate-spin" /></div>}>
                    <AIPeopleAdvisor />
                  </Suspense>
                </ProtectedRoute>
              } />
              {/* Sport Hub & Tournament — new paths */}
              <Route path="/sport" element={
                <ProtectedRoute>
                  <Suspense fallback={<div className="flex items-center justify-center min-h-screen"><Loader2 className="animate-spin" /></div>}>
                    <SportPickleball />
                  </Suspense>
                </ProtectedRoute>
              } />
              <Route path="/sport/tournament" element={
                <ProtectedRoute>
                  <Suspense fallback={<div className="flex items-center justify-center min-h-screen"><Loader2 className="animate-spin" /></div>}>
                    <PickleballTournament />
                  </Suspense>
                </ProtectedRoute>
              } />
              {/* Old paths redirect */}
              <Route path="/sport_pickleball" element={<Navigate to="/sport" replace />} />
              <Route path="/sport_pickleball/tournament" element={<Navigate to="/sport/tournament" replace />} />
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<ProtectedRoute><NotFound /></ProtectedRoute>} />
            </Routes>
          </BrowserRouter>
        </TooltipProvider>
        </ForceRefreshProvider>
        </FeatureFlagProvider>
      </AuthProvider>
    </QueryClientProvider>
  </AppErrorBoundary>
  );
};

export default App;
